import { OfflineDatabase } from "../database";
import { SyncChange, STORES } from "../schema";
import { v4 as uuidv4 } from "uuid";

export class SyncQueueStore {
  constructor(private db: OfflineDatabase) {}

  // Add a pending change to the queue
  async add(
    change:
      | Omit<SyncChange, "id" | "createdAt" | "retryCount" | "status">
      | SyncChange,
  ): Promise<string> {
    // If full SyncChange provided (with id), use it directly
    if ("id" in change && change.id) {
      await this.db.set(STORES.SYNC_QUEUE, change as SyncChange);
      return change.id;
    }

    // Otherwise, create new entry with defaults
    const id = uuidv4();
    const entry: SyncChange = {
      ...change,
      id,
      createdAt: Date.now(),
      retryCount: 0,
      status: "pending",
    };

    await this.db.set(STORES.SYNC_QUEUE, entry);
    return id;
  }

  // Get all changes from the queue
  async getAll(): Promise<SyncChange[]> {
    return this.db.getAll<SyncChange>(STORES.SYNC_QUEUE);
  }

  // Update a specific change
  async update(id: string, updates: Partial<SyncChange>): Promise<void> {
    const entry = await this.db.get<SyncChange>(STORES.SYNC_QUEUE, id);
    if (entry) {
      const updated = { ...entry, ...updates };
      await this.db.set(STORES.SYNC_QUEUE, updated);
    }
  }

  // Get all pending changes (not syncing/failed)
  async getPending(): Promise<SyncChange[]> {
    const all = await this.db.queryByIndex<SyncChange>(
      STORES.SYNC_QUEUE,
      "status",
      "pending",
    );
    return all.sort((a, b) => a.createdAt - b.createdAt);
  }

  // Get changes by entity ID
  async getByEntityId(entityId: string): Promise<SyncChange[]> {
    const all = await this.getAll();
    return all.filter((change) => change.entityId === entityId);
  }

  // Remove syncing changes for a specific entity
  // IMPORTANT: Only removes "syncing" changes, keeps "pending" (new edits during sync) and "failed" (for retry)
  async removeByEntityId(entityId: string): Promise<void> {
    const changes = await this.getByEntityId(entityId);
    // Filter to only remove "syncing" changes
    const syncingChanges = changes.filter((c) => c.status === "syncing");
    if (syncingChanges.length > 0) {
      await this.remove(syncingChanges.map((c) => c.id));
    }
  }

  // Get changes by pre-order ID
  // For new schema: returns pre-order changes + flow changes that belong to this pre-order
  async getByPreOrderId(preOrderId: string): Promise<SyncChange[]> {
    const all = await this.db.getAll<SyncChange>(STORES.SYNC_QUEUE);
    return all.filter(
      (change) =>
        (change.entityType === "pre-order" && change.entityId === preOrderId) ||
        (change.entityType === "flow" &&
          change.data.pre_order_id === preOrderId),
    );
  }

  // Get changes by flow ID
  async getByFlowId(flowId: string): Promise<SyncChange[]> {
    const all = await this.db.getAll<SyncChange>(STORES.SYNC_QUEUE);
    return all.filter(
      (change) => change.entityType === "flow" && change.entityId === flowId,
    );
  }

  // Get all failed changes (for retry)
  async getFailed(): Promise<SyncChange[]> {
    return this.db.queryByIndex<SyncChange>(
      STORES.SYNC_QUEUE,
      "status",
      "failed",
    );
  }

  // Get all syncing changes
  async getSyncing(): Promise<SyncChange[]> {
    return this.db.queryByIndex<SyncChange>(
      STORES.SYNC_QUEUE,
      "status",
      "syncing",
    );
  }

  // Mark changes as syncing
  async markSyncing(ids: string[]): Promise<void> {
    for (const id of ids) {
      const entry = await this.db.get<SyncChange>(STORES.SYNC_QUEUE, id);
      if (entry) {
        entry.status = "syncing";
        await this.db.set(STORES.SYNC_QUEUE, entry);
      }
    }
  }

  // Mark change as failed with retry info
  async markFailed(id: string): Promise<void> {
    const entry = await this.db.get<SyncChange>(STORES.SYNC_QUEUE, id);
    if (entry) {
      entry.status = "failed";
      entry.retryCount += 1;
      entry.lastRetryAt = Date.now();
      await this.db.set(STORES.SYNC_QUEUE, entry);
    }
  }

  // Reset failed changes to pending (for retry)
  async resetFailed(id: string): Promise<void> {
    const entry = await this.db.get<SyncChange>(STORES.SYNC_QUEUE, id);
    if (entry) {
      entry.status = "pending";
      await this.db.set(STORES.SYNC_QUEUE, entry);
    }
  }

  // Remove synced changes
  async remove(ids: string[]): Promise<void> {
    await Promise.all(ids.map((id) => this.db.delete(STORES.SYNC_QUEUE, id)));
  }

  // Remove all changes for a pre-order
  async removeByPreOrderId(preOrderId: string): Promise<void> {
    const changes = await this.getByPreOrderId(preOrderId);
    await this.remove(changes.map((c) => c.id));
  }

  // Update flowId for all pending changes (used when temp ID is replaced with real ID)
  async updateFlowId(oldFlowId: string, newFlowId: string): Promise<void> {
    const changes = await this.getByFlowId(oldFlowId);
    for (const change of changes) {
      // Update entityId for flow changes
      if (change.entityType === "flow") {
        change.entityId = newFlowId;
        await this.db.set(STORES.SYNC_QUEUE, change);
      }
    }
  }

  // Update preOrderId for all pending changes (used when temp ID is replaced with real ID)
  async updatePreOrderId(
    oldPreOrderId: string,
    newPreOrderId: string,
  ): Promise<void> {
    const changes = await this.getByPreOrderId(oldPreOrderId);
    for (const change of changes) {
      // Update entityId for pre-order changes
      if (change.entityType === "pre-order") {
        change.entityId = newPreOrderId;
      }
      // Update data.pre_order_id for flow changes
      if (
        change.entityType === "flow" &&
        change.data.pre_order_id === oldPreOrderId
      ) {
        change.data.pre_order_id = newPreOrderId;
      }
      await this.db.set(STORES.SYNC_QUEUE, change);
    }
  }

  // Get total pending count (for UI badge)
  async getPendingCount(): Promise<number> {
    const pending = await this.getPending();
    const failed = await this.getFailed();
    return pending.length + failed.length;
  }

  // Check if there are unsaved changes
  async hasUnsavedChanges(): Promise<boolean> {
    const count = await this.getPendingCount();
    return count > 0;
  }

  // Clear all (for reset/logout)
  async clear(): Promise<void> {
    const all = await this.db.getAll<SyncChange>(STORES.SYNC_QUEUE);
    await this.remove(all.map((c) => c.id));
  }

  // Get changes ready for retry (respects exponential backoff)
  async getReadyForRetry(): Promise<SyncChange[]> {
    const failed = await this.getFailed();
    const now = Date.now();

    return failed.filter((change) => {
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 32s, 64s, max 2min
      const backoffMs = Math.min(Math.pow(2, change.retryCount) * 1000, 120000);
      const nextRetryAt = (change.lastRetryAt || 0) + backoffMs;
      return now >= nextRetryAt;
    });
  }
}
